import { Check, Circle, Eye, EyeOff, Sparkles } from 'lucide-react';
import { forwardRef, useState, type FocusEventHandler } from 'react';
import { cn } from './cn.js';
import { Input } from './input.js';
import { generateStrongPassword } from './password-generator.js';

const passwordRules = [
  { label: '8 ou mais caracteres', test: (value: string) => value.length >= 8 },
  { label: 'Uma letra maiúscula', test: (value: string) => /[A-Z]/.test(value) },
  { label: 'Um número', test: (value: string) => /\d/.test(value) },
  { label: 'Um caractere especial', test: (value: string) => /[^A-Za-z0-9]/.test(value) },
] as const;

const strengthLabels = ['Muito fraca', 'Fraca', 'Razoável', 'Forte', 'Muito forte'] as const;
const strengthColors = [
  'bg-danger',
  'bg-orange-400',
  'bg-warning',
  'bg-brand-400',
  'bg-positive',
] as const;

export interface PasswordStrengthInputProps {
  label: string;
  value: string;
  onValueChange: (value: string) => void;
  onBlur?: FocusEventHandler<HTMLInputElement> | undefined;
  name?: string | undefined;
  error?: string | undefined;
  autoComplete?: string | undefined;
  disabled?: boolean | undefined;
}

export const PasswordStrengthInput = forwardRef<HTMLInputElement, PasswordStrengthInputProps>(
  (
    { label, value, onValueChange, onBlur, name, error, autoComplete = 'new-password', disabled },
    ref,
  ) => {
    const [visible, setVisible] = useState(false);
    const strength = getPasswordStrength(value);

    return (
      <div className="space-y-3">
        <Input
          ref={ref}
          label={label}
          name={name}
          type={visible ? 'text' : 'password'}
          value={value}
          onChange={(event) => onValueChange(event.target.value)}
          onBlur={onBlur}
          autoComplete={autoComplete}
          disabled={disabled}
          error={error}
          rightIcon={
            <button
              type="button"
              onClick={() => setVisible((current) => !current)}
              aria-label={visible ? 'Ocultar senha' : 'Mostrar senha'}
              className="grid size-10 place-items-center rounded-lg transition hover:text-content focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
            >
              {visible ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
            </button>
          }
        />

        <div className="rounded-xl border border-surface-border bg-surface-elevated/45 p-3">
          <div className="flex items-center justify-between gap-3">
            <span className="text-[11px] font-semibold text-content-secondary">Força da senha</span>
            <span className="text-[11px] font-semibold text-content/75">
              {strengthLabels[strength.level]}
            </span>
          </div>
          <div
            role="progressbar"
            aria-label="Força da senha"
            aria-valuemin={0}
            aria-valuemax={4}
            aria-valuenow={strength.level}
            className="mt-2 grid grid-cols-5 gap-1"
          >
            {strengthLabels.map((_, index) => (
              <span
                key={index}
                className={cn(
                  'h-1 rounded-full transition-colors',
                  index <= strength.level && value
                    ? strengthColors[strength.level]
                    : 'bg-surface-border',
                )}
              />
            ))}
          </div>
          <div className="mt-3 grid gap-1.5 sm:grid-cols-2">
            {passwordRules.map((rule) => {
              const valid = rule.test(value);
              return (
                <span
                  key={rule.label}
                  data-valid={String(valid)}
                  className={cn(
                    'flex items-center gap-1.5 text-[10px] transition-colors',
                    valid ? 'text-positive' : 'text-content-faint',
                  )}
                >
                  {valid ? <Check className="size-3" /> : <Circle className="size-2.5" />}
                  {rule.label}
                </span>
              );
            })}
          </div>
        </div>

        <button
          type="button"
          disabled={disabled}
          onClick={() => onValueChange(generateStrongPassword())}
          className="flex min-h-10 items-center gap-2 rounded-lg px-2 py-2 text-xs font-semibold text-brand-400 transition hover:text-brand-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50 disabled:opacity-50"
        >
          <Sparkles className="size-3.5" />
          Sugerir senha forte
        </button>
      </div>
    );
  },
);
PasswordStrengthInput.displayName = 'PasswordStrengthInput';

function getPasswordStrength(value: string): { level: 0 | 1 | 2 | 3 | 4 } {
  let score = passwordRules.filter((rule) => rule.test(value)).length;
  if (/[a-z]/.test(value)) score += 1;
  if (value.length >= 12) score += 1;
  if (value.length >= 16) score += 1;
  const level =
    value.length === 0
      ? 0
      : score <= 1
        ? 0
        : score === 2
          ? 1
          : score === 3
            ? 2
            : score <= 5
              ? 3
              : 4;
  return { level };
}
